import { Citation } from 'api';

export type HighlightRange = { start: number; stop: number };

/** The ranges to highlight for every one of a field's model citations. Skips
 * ids with no citation row and ranges outside the text, and merges overlaps.
 * Sorted by start, so the first range is the one to scroll to. */
export const computeHighlightRanges = (
  citationIds: number[],
  citationsById: Map<number, Citation>,
  textLength: number
): HighlightRange[] => {
  const valid: HighlightRange[] = [];
  citationIds.forEach(id => {
    const citation = citationsById.get(id);
    if (!citation) return;
    if (citation.start < 0 || citation.stop > textLength || citation.stop <= citation.start) {
      return;
    }
    valid.push({ start: citation.start, stop: citation.stop });
  });

  valid.sort((a, b) => a.start - b.start);

  const merged: HighlightRange[] = [];
  valid.forEach(range => {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.stop) {
      last.stop = Math.max(last.stop, range.stop);
    } else {
      merged.push({ ...range });
    }
  });
  return merged;
};

/** Splits the text into segments tagged with which layers cover them: the
 * model's citations and the reviewer's references, so each keeps its own colour
 * where they overlap. The segments concatenate back to `text` exactly; the
 * source pane's selection offsets depend on that. */
export type Segment = { text: string; llm: boolean; override: boolean };

export const buildSegments = (
  text: string,
  llmRanges: HighlightRange[],
  overrideRanges: HighlightRange[]
): Segment[] => {
  const points = new Set<number>([0, text.length]);
  [...llmRanges, ...overrideRanges].forEach(r => {
    points.add(r.start);
    points.add(r.stop);
  });

  const sorted = Array.from(points)
    .filter(p => p >= 0 && p <= text.length)
    .sort((a, b) => a - b);

  const covers = (ranges: HighlightRange[], start: number, stop: number) =>
    ranges.some(r => start >= r.start && stop <= r.stop);

  const segments: Segment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const stop = sorted[i + 1];
    if (start >= stop) continue;
    segments.push({
      text: text.slice(start, stop),
      llm: covers(llmRanges, start, stop),
      override: covers(overrideRanges, start, stop),
    });
  }
  return segments;
};
